// ScoreCard (Components, BL-189): one score against its goal, as a card. A head (a name beside its pen,
// and a muted line), the headline Figure, a Meter against the goal or the target with its own head
// ("Conversion 42 %"), where it stands in a Label and the rest of that sentence, up to six small facts
// (Figures, sm) and a muted foot. The whole card can open (a button, as Button behaves: Enter,
// Space, a click), or carry its actions under it (a chat's way into Flowology). It replaced GoalPace,
// which is now a ScoreCard of gp.pace's answer (views.tsx). It adds only its own layout: PenKey, Figure,
// Meter, Label and Figures draw their parts. Reads only --score-*.
import type { ReactNode } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { Figure, Figures, type FigureItem } from '../Figures/Figures.tsx';
import { Label, type LabelKind } from '../Label/Label.tsx';
import { Meter, type MeterProps } from '../Meter/Meter.tsx';
import { PenKey } from '../PenKey/PenKey.tsx';
import type { Pen } from '../pen.ts';
import './ScoreCard.css';

export interface ScoreCardProps {
  /** Whose score: their name beside their pen. Without a name the card has no head. */
  name?: string;
  pen?: Pen | null;
  /** The muted line under the name ("Drains, started this year"). */
  line?: string;
  /** The headline: what it is, the figure and the line under it. */
  figure?: { label: string; value: ReactNode; sub?: ReactNode };
  /** The bar against the goal: its head (a word and its figure), the Meter, or a sentence when there's none. */
  meter?: { head?: string; value?: string; bar?: MeterProps; none?: string };
  /** Where it stands: a Label, then the rest of the sentence ("Behind pace" "by $2,300."). */
  status?: { word: string; kind: LabelKind; rest?: string };
  /** Up to six small facts (Figures, sm). */
  facts?: FigureItem[];
  /** A muted line at the foot ("Gross profit $4,200, 32 %"). */
  foot?: ReactNode;
  /** Nothing to score: one muted sentence in place of the figure, the bar and the facts. */
  empty?: string;
  /** The whole card opens; label is what it says to a screen reader. */
  open?: { onPress: () => void; label?: string };
  /** Under the card: its actions (a chat's "Open in Flowology"). */
  actions?: ReactNode;
  /** Its name for a screen reader, when the card doesn't open. */
  label?: string;
}

function Body(p: ScoreCardProps) {
  return (
    <>
      {p.name != null && (
        <span className="fl-score__head">
          <span className="fl-score__name">
            <PenKey pen={p.pen ?? null}>{p.name}</PenKey>
          </span>
          {p.line && <span className="fl-score__line">{p.line}</span>}
        </span>
      )}
      {p.empty ? (
        <span className="fl-score__none">{p.empty}</span>
      ) : (
        <>
          {p.figure && <Figure label={p.figure.label} value={p.figure.value} sub={p.figure.sub} />}
          {p.meter && (
            <span className="fl-score__meter">
              {p.meter.head && <span className="fl-score__mhead">{p.meter.head}</span>}
              {p.meter.value && <span className="fl-score__mvalue">{p.meter.value}</span>}
              {p.meter.bar ? (
                <span className="fl-score__bar">
                  <Meter {...p.meter.bar} />
                </span>
              ) : p.meter.none ? (
                <span className="fl-score__mnone">{p.meter.none}</span>
              ) : null}
            </span>
          )}
          {p.status && (
            <span className="fl-score__status">
              <Label kind={p.status.kind}>{p.status.word}</Label>
              {p.status.rest}
            </span>
          )}
          {p.facts?.length ? (
            <span className="fl-score__facts">
              <Figures size="sm" items={p.facts} />
            </span>
          ) : null}
          {p.foot != null && <span className="fl-score__foot">{p.foot}</span>}
        </>
      )}
    </>
  );
}

export function ScoreCard(p: ScoreCardProps) {
  if (p.open)
    return (
      <AriaButton className="fl-score" data-opens="" onPress={p.open.onPress} aria-label={p.open.label}>
        <Body {...p} />
      </AriaButton>
    );
  return (
    <article className="fl-score" aria-label={p.label}>
      <Body {...p} />
      {p.actions && <div className="fl-score__acts">{p.actions}</div>}
    </article>
  );
}
